import { useMutation } from '@apollo/client';
import * as _ from 'lodash-es';
import React from 'react';
import * as styles from './EnableWithTokenModal.module.scss';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Callout from '../../components/CallOut';
import Form, { Field, Submit } from '../../components/Form';
import LoadingIndicator from '../../components/LoadingIndicator';
import Modal, { ModalActionFooter, ModalTitle } from '../../components/Modal';
import CREATE_STATIC_TOKEN_INTEGRATION from '../../graphql/createStaticTokenIntegration';
import { UIActions } from '../../ui/uiSlice';

type PlatformKey =
  | 'bamboohr'
  | 'shortcut'
  | 'freshdesk'
  | 'monday'
  | 'quip'
  | 'todoist'
  | 'guru';

type ExtraFieldKey = 'host' | 'email';

type ExtraField = {
  name: ExtraFieldKey;
  defaultValueName?: ExtraFieldKey;
  label: string;
  placeholder: string;
  prepend?: string;
  append?: string;
  required: boolean;
};

type Values = {
  token: string;
  host?: string;
  email?: string;
};

type Platform = {
  name: string;
  description: JSX.Element;
  extraFields?: readonly ExtraField[];
  preProcess?: (input: Values) => {
    host: string;
    token: string;
  };
};

const PLATFORMS: Record<PlatformKey, Platform> = {
  bamboohr: {
    name: 'BambooHR',
    description: (
      <>
        Slab supports importing users along with their attributes from BambooHR.
        To fetch that information for you, we need access to BambooHR&apos;s API
        using your API token.
        <ol>
          <li>Log in to BambooHR</li>
          <li>
            Click on your account avatar in the top right corner, and open{' '}
            <i>API Keys</i> in the dropdown menu
          </li>
          <li>
            Select <i>Add New Key</i> and name it <i>Slab</i>
          </li>
          <li>
            Click on the <b>Generate Token</b> button, copy the token and paste
            it below
          </li>
        </ol>
      </>
    ),
    extraFields: [
      {
        name: 'host',
        label: 'BambooHR subdomain',
        placeholder: 'subdomain',
        prepend: 'https://',
        append: '.bamboohr.com',
        required: true,
      },
    ],
  },
  shortcut: {
    name: 'Shortcut',
    description: (
      <>
        Slab supports rich previews of Shortcut stories and epics. To fetch that
        information for you, we need access to Shortcut&apos;s API using your
        API token. This token is in your Shortcut settings:
        <ol>
          <li>Open Shortcut</li>
          <li>
            Click on your account avatar in the bottom left corner, and open{' '}
            <i>Settings</i> in the dropdown menu
          </li>
          <li>
            Select the <i>API Tokens</i> option and in the textbox titled{' '}
            <i>Token Name</i>, enter &quot;Slab&quot;
          </li>
          <li>
            Click on the <b>Generate Token</b> button, copy the token and paste
            it below
          </li>
        </ol>
      </>
    ),
  },
  freshdesk: {
    name: 'Freshdesk',
    description: (
      <>
        Slab supports rich previews of Freshdesk tickets. To fetch that
        information for you, we need access to Freshdesk&apos;s API using your
        API token. This token is in your Freshdesk user profile:
        <ol>
          <li>Open your Freshdesk dashboard</li>
          <li>
            Click on your avatar at the top right corner, and select{' '}
            <i>Profile Settings</i> in the menu
          </li>
          <li>
            In the right sidebar, copy <b>Your API Key</b>, and paste it below
            along with your Freshdesk subdomain
          </li>
        </ol>
      </>
    ),
    extraFields: [
      {
        name: 'host',
        label: 'Freshdesk subdomain',
        placeholder: 'subdomain',
        prepend: 'https://',
        append: '.freshdesk.com',
        required: true,
      },
    ],
    preProcess: ({ host, token }) => ({
      host: `${host ?? ''}.freshdesk.com`,
      token,
    }),
  },
  monday: {
    name: 'Monday',
    description: (
      <>
        Slab supports rich previews of Monday boards and items. To fetch that
        information for you, we need access to Monday&apos;s API using your API
        token. This token is in your Monday settings:
        <ol>
          <li>Open Monday</li>
          <li>
            Click on your avatar at bottom left corner, and select{' '}
            <i>Administration</i> in the menu
          </li>
          <li>
            In the <i>API</i> section, under <i>API v2 Token</i>, generate and
            copy your <b>Personal API Token</b> and paste it below
          </li>
        </ol>
      </>
    ),
  },
  quip: {
    name: 'Quip',
    description: (
      <>
        <p>
          Slab supports searching your team&apos;s Quip for documents,
          spreadsheets and more. To do that, we need access to Quip&apos;s API
          using your Personal API token:
        </p>
        <Callout variant="warning">
          Slab will search all documents you can access, including unshared
          ones. You may want to create a new Quip user dedicated to providing
          search to Slab.
        </Callout>
        <ol>
          <li>Open Quip and Log in</li>
          <li>
            Navigate to <i>https://yourdomain.quip.com/dev/token</i> in your
            browser
          </li>
          <li>
            Click on the <b>Get Access Token</b> button to generate a new API
            token
          </li>
          <li>Copy the token and paste it below</li>
        </ol>
      </>
    ),
  },
  todoist: {
    name: 'Todoist',
    description: (
      <>
        Slab supports rich previews of Todoist tasks. To fetch that information
        for you, we need access to Todoist&apos;s API using your API token. This
        token is in your Todoist settings:
        <ol>
          <li>Open Todoist</li>
          <li>
            Click on your account avatar in the top left corner, and open{' '}
            <i>Settings</i> in the dropdown menu
          </li>
          <li>
            Click on <i>Integrations</i>
          </li>
          <li>
            Click on the <i>Developer</i> tab
          </li>
          <li>
            Copy the token under <b>API token</b> and paste it below
          </li>
        </ol>
      </>
    ),
  },
  guru: {
    name: 'Guru',
    description: (
      <>
        Slab supports rich previews of Guru cards. To fetch that information for
        you, we need access to Guru&apos;s API using your Guru user token and
        email address. The token is in your Guru settings:
        <ol>
          <li>Open Guru</li>
          <li>
            Click on your account avatar in the top right corner, and open{' '}
            <i>Team settings</i> in the dropdown menu
          </li>
          <li>
            Click on <i>Apps and Integrations</i> in the sidebar and select{' '}
            <i>API Access</i> tab
          </li>
          <li>
            Click on the <b>Generate user token</b> button, generate the token
            and paste it below
          </li>
        </ol>
      </>
    ),
    extraFields: [
      {
        name: 'email',
        defaultValueName: 'email',
        label: 'You email address in Guru',
        placeholder: 'email',
        required: true,
      },
    ],
  },
};

export const usesStaticToken = (platform: string): platform is PlatformKey =>
  !!PLATFORMS[platform as PlatformKey];

interface EnableWithTokenModalProps {
  platform: PlatformKey;
  close: () => void;
  redirectUrl?: string;
}

const EnableWithTokenModal = ({
  platform,
  close,
  redirectUrl,
}: EnableWithTokenModalProps) => {
  const dispatch = useAppDispatch();
  const { name, description, extraFields, preProcess } = PLATFORMS[platform];
  const user = useAppSelector((state) => state.session.user);

  const [integrate] = useMutation(CREATE_STATIC_TOKEN_INTEGRATION);

  const handleInvalidToken = () =>
    dispatch(
      UIActions.pushToast({
        level: 'error',
        message: `Please enter a valid ${name} API key`,
      }),
    );

  const handleSubmit = async (params: Values) => {
    try {
      const paramsProcessed = preProcess?.(params) ?? params;
      await integrate({ variables: { platform, ...paramsProcessed } });
      // @ts-expect-error Fix next time editing the file
      document.location = redirectUrl || document.location;
      return true;
    } catch (error) {
      return handleInvalidToken();
    }
  };

  const defaultValues: Record<ExtraFieldKey, string> = {
    email: user.email ?? '',
    host: '',
  };

  const initialValues: Values = { token: '' };
  (extraFields || []).forEach((field) => {
    initialValues[field.name] = field.defaultValueName
      ? defaultValues[field.defaultValueName]
      : '';
  });

  return (
    <Modal width={500}>
      <ModalTitle close={close}>Enable {name}</ModalTitle>

      <div className={styles.getApiKey}>
        <strong>Get your API Key</strong>
      </div>

      <div className={styles.description}>{description}</div>

      <Form<Values> initialValues={initialValues} onSubmit={handleSubmit}>
        {({ isSubmitting }) => (
          <>
            <Field
              name="token"
              label="Enter it here"
              placeholder="API Key"
              required
            />

            {(extraFields || []).map((fieldProps) => (
              <Field
                key={fieldProps.name}
                {..._.omit(fieldProps, 'defaultValueName')}
              />
            ))}

            <ModalActionFooter
              primary={
                <Submit>
                  {isSubmitting ? (
                    <LoadingIndicator delay={0} adaptColor />
                  ) : (
                    'Enable Integration'
                  )}
                </Submit>
              }
            />
          </>
        )}
      </Form>
    </Modal>
  );
};

export default EnableWithTokenModal;
